import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * A custom React hook that provides a boolean state which automatically resets to false after a specified delay.
 *
 * This hook is useful for temporary UI states like notifications, tooltips, or feedback messages
 * that should automatically disappear after a certain time.
 *
 * @param {number} [delay=1500] - The time in milliseconds after which the boolean value will reset to false.
 * @returns {[boolean, () => void]} - A tuple containing:
 *   - The current boolean state value
 *   - A function to trigger the timeout (sets the value to true and starts the timeout to reset it)
 */
export function useBooleanTimeout(delay = 1500) {
  const [value, setValue] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const triggerTimeout = useCallback(() => {
    setValue(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setValue(false);
    }, delay);
  }, [setValue, timeoutRef, delay]);

  // Cleanup the timeout if the component using the hook unmounts.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [value, triggerTimeout] as const;
}
